import React, { useState, useEffect } from "react";
import { MapPin, Menu, Search, X } from "lucide-react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Drawer from "./Drawer";
import { useTranslation } from "react-i18next";

const SEARCH_HINTS = [
  "paneer",
  "milk",
  "atta",
  "dal",
  "rice",
  "bread",
  "butter",
  "eggs",
];

const Header = ({ searchQuery, setSearchQuery, categoryName = "products" }) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setHintIndex(i => (i + 1) % SEARCH_HINTS.length), 2000);
    return () => clearInterval(id);
  }, []);
  const userAddress = useSelector(
    (state) => state.user.userInfo?.defaultAddress
  );
  const martInfo = userAddress?.nearby;

  return (
    <header className="shadow-sm sticky top-0 z-10 border-b flex flex-col gap-4 py-3 px-4 bg-primary-50 dark:bg-background-200">
      <div className="flex justify-between items-center gap-2">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
        >
          <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
        <Link to="/" className="min-w-max">
          <img src="/assets/appFullLogo.png" alt="App Logo" className="h-10" />
        </Link>
        <div className="flex items-center justify-between w-full ml-2">
          <div className="flex items-center space-x-2 rounded-lg flex-grow mr-2">
            <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div className="flex flex-col">
              <Link
                to="/addresses"
                className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-1 [overflow-wrap:anywhere]"
              >
                {martInfo ? (
                  <>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      {martInfo.nearbyLocation}, {martInfo.city}, {martInfo.state} - {martInfo.pinCode}
                    </span>
                  </>
                ) : (
                  t('header.setAddress')
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div>
          <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder=""
            className="w-full pl-10 pr-10 py-2 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300" />

          {/* Animated placeholder — only when empty and not focused */}
          {!searchQuery && !searchFocused && (
            <div className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 flex items-center gap-1 overflow-hidden h-5">
              <span className="text-sm text-gray-400 dark:text-gray-400 whitespace-nowrap">Search</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={hintIndex}
                  initial={{ y: 16, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -16, opacity: 0 }}
                  transition={{ duration: 0.28, ease: "easeInOut" }}
                  className="text-sm text-gray-400 dark:text-gray-400 whitespace-nowrap"
                >
                  &quot;{SEARCH_HINTS[hintIndex]}&quot;
                </motion.span>
              </AnimatePresence>
            </div>
          )}

          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
};

export default Header;
